/**
 * Spinner atom — minimal loading indicator.
 *
 * Atomic hierarchy:
 * - Atom (single-purpose visual primitive)
 *
 * Purpose:
 * - Communicate indeterminate loading in compact UI spaces.
 *
 * Accessibility:
 * - Uses `role="status"` and an accessible label.
 */
import React from "react";
import { cn } from "@/utils/cn";
import {
  dotSizeClasses,
  getDotToneClass,
  getHalfRingArcColorClass,
  gradientPaddingClasses,
  intentClasses,
  pulseSizeClasses,
  ringStrokeClasses,
  sizeClasses,
} from "./Spinner.styles";
import type { SpinnerProps } from "./Spinner.types";

export const Spinner: React.FC<SpinnerProps> = ({
  size = "md",
  variant = "gradient-ring",
  thickness = "bold",
  intent = "primary",
  className,
  "aria-label": ariaLabel = "Loading",
}) => {
  const strokeClass = ringStrokeClasses[thickness][size];
  const gradientPaddingClass = gradientPaddingClasses[thickness][size];
  const dotTone = getDotToneClass(intent);

  if (variant === "dots") {
    return (
      <div role="status" aria-label={ariaLabel} className={cn("flex items-center gap-1.5", className)}>
        <span className={cn("animate-bounce rounded-full", dotSizeClasses[size], dotTone)} />
        <span className={cn("animate-bounce rounded-full [animation-delay:120ms]", dotSizeClasses[size], dotTone)} />
        <span className={cn("animate-bounce rounded-full [animation-delay:240ms]", dotSizeClasses[size], dotTone)} />
      </div>
    );
  }

  if (variant === "pulse") {
    return (
      <div role="status" aria-label={ariaLabel} className={cn("relative", pulseSizeClasses[size], className)}>
        <span className={cn("absolute inset-0 rounded-full animate-ping opacity-30", dotTone)} />
        <span className={cn("absolute inset-[30%] rounded-full", dotTone)} />
      </div>
    );
  }

  if (variant === "gradient-ring") {
    return (
      <div
        role="status"
        aria-label={ariaLabel}
        className={cn(
          "animate-spin rounded-full border-transparent bg-gradient-to-r from-blue via-purple to-blue [mask:linear-gradient(#fff_0_0)_content-box,linear-gradient(#fff_0_0)] [mask-composite:exclude]",
          sizeClasses[size],
          gradientPaddingClass,
          className,
        )}
      />
    );
  }

  if (variant === "half-ring") {
    const outerArcColor = getHalfRingArcColorClass(intent);

    return (
      <div
        role="status"
        aria-label={ariaLabel}
        className={cn(
          "relative animate-spin",
          sizeClasses[size],
          className,
        )}
      >
        <span
          aria-hidden
          className={cn(
            "absolute inset-0 rounded-full border-r-transparent border-t-transparent",
            outerArcColor,
            strokeClass,
          )}
        />
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-label={ariaLabel}
      className={cn(
        "animate-spin rounded-full",
        sizeClasses[size],
        intentClasses[intent],
        strokeClass,
        className,
      )}
    />
  );
};

